import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Col, Card, CardBody } from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import { getQuotes, deleteQuote, getStripeSetupLink } from "../../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useLocation, useNavigate } from "react-router-dom";
import { clearQuoteDetails, selectQuotesState } from "slices/quotes/reducer";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import AlertModal from "Components/Modals/AlertModal";
import QuoteListFilter from "./QuoteListFilter";
import ActionMenu from "Components/Common/ActionMenu";
import { getAvatarStyle, getInitials } from "utils/getAvatarColor";
import { getStatusBadge } from "utils/getStatusBatch";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";
import { clearItemState } from "slices/items/reducer";
import { useAppSelector } from "Components/Hooks/hooks";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import { getLastNDaysRange } from "utils/utils";
import Icon, { IconName } from "Components/Icons/Icon";
import { ErrorToast, ErrorHandler } from "utils/toast";

export interface QuoteListFilterState {
  startDate: string | null;
  endDate: string | null;
  status: string[];
}

const QuoteList = () => {
  const { state } = useLocation();
  const [selectQuoteId, setSelectQuoteId] = useState<number | null>(null);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);
  const user = useAppSelector((state) => state.auth.user);

  const [showStripeConnectModal, setShowStripeConnectModal] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const storageKey = `stripe_connect_prompt_shown_${user?.id || ""}`;
  const [hasShownPrompt, setHasShownPrompt] = useState(() => {
    return localStorage.getItem(storageKey) === "true";
  });

  const handleStripeConnect = async () => {
    if (user?.stripe_account_status === "active" || user?.stripe_connected) return;

    try {
      setStripeLoading(true);
      const result = await dispatch(getStripeSetupLink()).unwrap();
      const redirectUrl = result?.payload?.url;
      setShowStripeConnectModal(false);
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setStripeLoading(false);
    }
  };

  const dismissModal = () => {
    setShowStripeConnectModal(false);
    if (user?.id) {
      localStorage.setItem(storageKey, "true");
      setHasShownPrompt(true);
    }
  };

  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;

  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => { },
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner."
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  const goToQuoteDetails = useCallback(
    (quoteId: number) => {
      navigate("/quote-details", { state: { id: quoteId } });
    },
    [navigate]
  );

  const [filters, setFilters] = useState<QuoteListFilterState>(
    state === 1
      ? {
        startDate: null,
        endDate: null,
        status: ["sent", "completed"],
      }
      : state === 2
        ? {
          ...getLastNDaysRange(),
          status: ["approved"],
        }
        : {
          startDate: null,
          endDate: null,
          status: [],
        }
  );

  const handleFilterChange = (data: QuoteListFilterState) => {
    setFilters(data);
    setShowFilter(false);
  };

  const quoteSelector = createSelector(selectQuotesState, (state) => ({
    quoteItems: state.quotes,
    isLoading: state.status.fetch,
    error: state.errors.fetch,
    meta: state.pagination,
  }));

  const { quoteItems, isLoading, meta } = useSelector(quoteSelector);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when filters or search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  // Fetch quotes when page, filters, or search changes
  useEffect(() => {
    dispatch(
      getQuotes({
        page: currentPage,
        search: searchTerm || undefined,
        start_date: filters.startDate,
        end_date: filters.endDate,
        status: filters.status,
      })
    );
  }, [currentPage, dispatch, filters, searchTerm]);

  useEffect(() => {
    if (
      isLoading !== "loading" &&
      quoteItems?.length === 1 &&
      !user?.stripe_connected &&
      user?.stripe_account_status !== "active" &&
      !hasShownPrompt &&
      user?.id
    ) {
      setShowStripeConnectModal(true);
      setHasShownPrompt(true);
      localStorage.setItem(storageKey, "true");
    }
  }, [isLoading, quoteItems, user, hasShownPrompt, storageKey]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    console.log("Page changed to:", page);
    setCurrentPage(page);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "TITLE",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue() ? cell.getValue() : "-"}
            </span>
          );
        },
      },
      {
        header: "QUOTE",
        accessorKey: "reference_number",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "CLIENT",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          let value = cell.getValue();
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <div className="avatar-circle" style={getAvatarStyle(value)}>
                  {getInitials(value)}
                </div>

                <div className="text-black2D fw-normal fs-14 font-primary">
                  {value ?? "-"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "AMOUNT",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {"£" + cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "STATUS",
        accessorKey: "status",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell: any) => getStatusBadge(cell.getValue()),
      },
      {
        header: "CREATION DATE",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cell?.getValue()) ?? "-"}
          </span>
        ),
      },
      {
        header: "EXPIRY DATE",
        accessorKey: "expiry_date",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cell?.getValue()) ?? "-"}
          </span>
        ),
      },
      {
        header: "ACTION",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionMenu
              onEdit={() => {
                if (hasNotSubscribed) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED
                  );
                } else if (!hasBankAccount && !hasSignature) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED
                  );
                } else if (!hasBankAccount) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED
                  );
                } else if (!hasSignature) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.SIGNATURE_REQUIRED
                  );
                } else {
                  dispatch(clearQuoteDetails());
                  dispatch(clearItemState());
                  navigate("/manage-quotes", {
                    state: { id: rowData.id },
                  });
                }
              }}
              onDelete={() => {
                setSelectQuoteId(rowData.id);
                setDeleteModal(true);
              }}
              isView
              onView={() => goToQuoteDetails(rowData.id)}
              isEdit={rowData.is_editable}
              isDelete={rowData.is_editable}
            />
          );
        },
      },
    ],
    [
      RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED,
      RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
      RESTRICTION_CONFIG.SIGNATURE_REQUIRED,
      RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED,
      dispatch,
      goToQuoteDetails,
      hasBankAccount,
      hasNotSubscribed,
      hasSignature,
      navigate,
    ]
  );

  return (
    <>
      <DeleteItemModal
        show={deleteModal}
        onCloseClick={() => setDeleteModal(false)}
        onDeleteClick={() => {
          if (selectQuoteId !== null) {
            dispatch(deleteQuote(selectQuoteId));
            setTimeout(() => {
              dispatch(
                getQuotes({
                  search: searchTerm,
                  page: currentPage,
                  start_date: filters.startDate,
                  end_date: filters.endDate,
                  status: filters.status,
                })
              );
            }, 1000);
          }
          setDeleteModal(false);
        }}
      />
      <Col xxl={12}>
        <Card id="quoteList" className="br-10 bg-white">
          <CardBody className="pt-0">
            <div>
              <TableContainer
                columns={columns}
                data={quoteItems}
                isGlobalFilter={true}
                customPageSize={meta?.per_page}
                divClass="table-responsive table-card mb-2 bg-white"
                tableClass="align-middle table-nowrap table-borderless"
                thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
                theadClass="table-light"
                SearchPlaceholder="Search quotes & clients"
                onSearch={handleSearch}
                onPageChange={handlePageChange}
                currentPage={currentPage}
                totalPages={meta?.last_page}
                toPage={meta?.to}
                fromPage={meta?.from}
                totalItems={meta?.total}
                isListFilter={true}
                onListFilter={() => setShowFilter(true)}
                isInitialLoading={
                  isLoading === "loading" && !quoteItems?.length
                }
                isPaginationLoading={
                  isLoading === "loading" && quoteItems?.length > 0
                }
              />
            </div>
          </CardBody>
        </Card>
      </Col>
      <QuoteListFilter
        show={showFilter}
        onCloseClick={() => setShowFilter(false)}
        onFilterChange={handleFilterChange}
        filters={filters}
      />
      <RestrictedAccessModal
        isOpen={showSubscriptionExpiredPopUp}
        onCancel={() => setShowSubscriptionExpiredPopUp(false)}
        iconName={restrictionModalData.iconName}
        title={restrictionModalData.title}
        description={restrictionModalData.description}
        buttonText={restrictionModalData.buttonText}
        onButtonClick={restrictionModalData.onAction}
      />
      <AlertModal
        isOpen={showStripeConnectModal}
        onCancel={dismissModal}
        onConfirm={handleStripeConnect}
        title="Start Accepting Online Payments"
        description="You've created your first quote. Connect Stripe to securely accept online payments from your customers and get paid faster."
        confirmText="Connect Stripe"
        cancelText="Maybe Later"
        isLoading={stripeLoading}
        icon={<Icon name="InfoPrimary" width={80} height={80} />}
      />
    </>
  );
};

export default QuoteList;
